/**
 * JobDetailDialog
 *
 * Modal that opens when a user clicks "View" on a job row in JobsEnhanced.
 * Four tabs:
 *   - Overview     : read-only summary of the job (title, customer, status,
 *                    assignee, address, due date, …)
 *   - Notes        : timestamped progress notes — anyone assigned can add
 *   - Attachments  : S3-backed file uploads (PDF / Office / images / ZIP)
 *   - Activity     : auto + manual activity feed, newest-first
 *
 * Backend routes (see /app/backend/routes/job_collab.py):
 *   POST   /api/jobs/{id}/notes              body: {text}
 *   DELETE /api/jobs/{id}/notes/{noteId}
 *   POST   /api/jobs/{id}/attachments        multipart: file
 *   DELETE /api/jobs/{id}/attachments/{aid}
 *   GET    /api/jobs/attachments/{filename}  → 302 → signed S3 URL
 *   GET    /api/jobs/{id}/activity           → [...]
 *
 * Permissions on writes: assignee, assigned-team members, admin, supervisor.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import JobLabelsEditor from './JobLabelsEditor';
import InvoiceEditorDialog from './InvoiceEditorDialog';
import MaterialsPanel from './MaterialsPanel';
import JobNotesPanel from './JobNotesPanel';
import TravelTimeControls from './TravelTimeControls';
import SiteQrDialog from './SiteQrDialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import CrewHoursPanel from './jobs/CrewHoursPanel';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  FileText, MessageSquare, Paperclip, Activity as ActivityIcon,
  Download, Trash2, Upload, Clock, ShieldAlert, FileSpreadsheet, FileArchive,
  Image as ImageIcon, X, User as UserIcon, MapPin, Calendar, Briefcase,
  Play, Pause, CheckCircle2, UserCog, Eye, EyeOff, UserPlus, Tag,
  DollarSign, Package, Receipt as ReceiptIcon, Link2, Copy, RotateCw, Send, ShieldCheck, AlertCircle, QrCode,
} from 'lucide-react';
import { toast } from 'sonner';
import { compressImage, isCompressibleImage } from '../utils/compressImage';
import { uploadJobAttachmentDirect } from '../utils/directUpload';
import Lightbox from './Lightbox';
import PhotoMetaBar, { photoDisplayName } from './PhotoMetaBar';
import AdminPhotoUploader, { RECEIVED_VIA_LABEL } from './AdminPhotoUploader';

const API = window.__BACKEND_URL__;

/* Photo grid thumbnail with tap-to-retry — a failed S3 load (weak signal)
   shows a retry tile instead of a silent broken-image icon. */
const PhotoThumb = ({ src, alt }) => {
  const [failed, setFailed] = React.useState(false);
  const [rk, setRk] = React.useState(0);
  if (failed) {
    return (
      <span
        role="button"
        tabIndex={0}
        onClick={(e) => { e.stopPropagation(); setFailed(false); setRk((k) => k + 1); }}
        className="w-full h-full flex flex-col items-center justify-center gap-1 bg-[#0F1923] text-[#FFA000]"
        data-testid="job-detail-photo-retry"
      >
        <AlertCircle className="w-5 h-5" />
        <span className="text-[10px] font-medium">Tap to retry</span>
      </span>
    );
  }
  return (
    <img
      key={rk}
      src={rk === 0 ? src : `${src}&_r=${rk}`}
      alt={alt}
      loading="lazy"
      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
      onError={() => setFailed(true)}
    />
  );
};

// ── helpers (kept local so this is a self-contained component) ───────────
const fmt = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })} · ${d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`;
};
const fmtFull = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit' });
};
const zipHref = (jobId) => {
  const token = localStorage.getItem('token') || '';
  const base = `${API}/api/jobs/${jobId}/attachments-zip`;
  return token && token !== 'cookie-session' && token !== 'null'
    ? `${base}?_t=${encodeURIComponent(token)}`
    : base;
};

const fmtSize = (b) => {
  if (!b) return '0 KB';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};
const attachmentHref = (a) => {
  if (!a?.filename) return '#';
  // Since the httpOnly-cookie migration the web app no longer keeps the JWT
  // in localStorage — the browser sends the ht_access cookie with the anchor
  // click instead. Only append ?_t= when a REAL token exists (native shell).
  const token = localStorage.getItem('token') || '';
  const base = `${API}/api/jobs/attachments/${a.filename}`;
  return token && token !== 'cookie-session' && token !== 'null'
    ? `${base}?_t=${encodeURIComponent(token)}`
    : base;
};
const extOf = (name = '') => {
  const m = String(name).toLowerCase().match(/\.([a-z0-9]+)$/);
  return m ? `.${m[1]}` : '';
};
const iconForAttachment = (a) => {
  const e = extOf(a?.original_name || a?.filename || '');
  if (['.png', '.jpg', '.jpeg', '.gif', '.webp', '.heic', '.heif'].includes(e)) return ImageIcon;
  if (['.xls', '.xlsx', '.ods', '.csv'].includes(e)) return FileSpreadsheet;
  if (e === '.zip') return FileArchive;
  return FileText;
};
const isArchive = (a) => extOf(a?.original_name || a?.filename || '') === '.zip';

// Map activity event types to a friendly icon for the timeline view.
// Falls back to MessageSquare for notes and Clock for anything unknown.
const ACTIVITY_ICON_MAP = {
  status_started: Play,
  status_completed: CheckCircle2,
  status_paused: Pause,
  status_resumed: Play,
  reassigned: UserCog,
  note_added: MessageSquare,
  note_deleted: MessageSquare,
  attachment_added: Paperclip,
  attachment_removed: Paperclip,
  note: MessageSquare,
};

// Subtle accent color per event type so a 30-event audit trail is scannable.
const ACTIVITY_COLOR = {
  status_started: '#1E88E5',
  status_completed: '#00C896',
  status_paused: '#F59E0B',
  status_resumed: '#1E88E5',
  reassigned: '#A855F7',
  note_added: '#94A3B8',
  note_deleted: '#64748B',
  attachment_added: '#06B6D4',
  attachment_removed: '#64748B',
  note: '#94A3B8',
};

// Tailwind-safe color palette for the per-user avatar bubbles in the timeline.
// We pick deterministically by hashing the user name so a person keeps the
// same color throughout the feed.
const AVATAR_PALETTE = [
  '#1E88E5', '#00C896', '#F59E0B', '#EF4444', '#A855F7',
  '#06B6D4', '#EC4899', '#84CC16', '#F97316', '#6366F1',
];

const colorForUser = (name) => {
  if (!name) return AVATAR_PALETTE[0];
  let h = 0;
  for (let i = 0; i < name.length; i += 1) {
    h = (h * 31 + name.charCodeAt(i)) >>> 0;
  }
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length];
};

const initialsOf = (name) => {
  if (!name) return '?';
  const parts = String(name).trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() || '').join('') || '?';
};

// Render-on-the-fly day separator label, e.g. "Today", "Yesterday", "Mar 15".
const dayLabel = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  const isSameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  if (isSameDay(d, today)) return 'Today';
  if (isSameDay(d, yesterday)) return 'Yesterday';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

// ── permission helper that mirrors backend _can_collab ───────────────────
const canCollab = (job, currentUser) => {
  if (!job || !currentUser) return false;
  if (['admin', 'supervisor'].includes(currentUser.role)) return true;
  if (job.assigned_to === currentUser.id) return true;
  if (Array.isArray(job.assigned_team_member_ids) && job.assigned_team_member_ids.includes(currentUser.id)) return true;
  if (job.assigned_team_id && currentUser.team_id === job.assigned_team_id) return true;
  return false;
};

export default function JobDetailDialog({ job, open, onClose, currentUser, onMutated }) {
  const [tab, setTab] = useState('overview');
  const [localJob, setLocalJob] = useState(job);
  const [activity, setActivity] = useState([]);
  const [loadingAct, setLoadingAct] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  // Watchers — list of [{id, name, email, role}]. Only admin/supervisor users.
  // Loaded when the dialog opens; managed via /api/jobs/{id}/watchers/* routes.
  const [watchers, setWatchers] = useState([]);
  const [watcherPickerOpen, setWatcherPickerOpen] = useState(false);
  const [companyAdmins, setCompanyAdmins] = useState([]); // for the invite dropdown
  // Resolved effective billing rate for this job — fetched on open & when
  // label_ids change. Used by the Overview "Effective rate" row so admins
  // can verify the rate the invoice will use before generating it.
  const [effectiveRate, setEffectiveRate] = useState(null);
  // Invoice Editor — admin can bill out from any job status (Phase 2)
  const [invoiceEditorOpen, setInvoiceEditorOpen] = useState(false);
  // Customer Portal — per-job public link (Jun 2026). Lazily loaded the
  // first time the admin opens the Portal tab; null = "not loaded yet",
  // empty object = "no token yet", populated = "active token".
  const [portalInfo, setPortalInfo] = useState(null);
  const [portalBusy, setPortalBusy] = useState(false);
  // Inline SMS-send state — admin types the customer's phone number once,
  // we E.164-validate and POST to the backend. We don't persist the phone
  // on the job (yet) because Customer records will own that field shortly.
  const [smsPhone, setSmsPhone] = useState('');
  const [smsBusy, setSmsBusy] = useState(false);
  // Invoice PDF viewer + job recall (Jun 2026)
  const [pdfBusy, setPdfBusy] = useState(false);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const [recallOpen, setRecallOpen] = useState(false);
  const [recallStatus, setRecallStatus] = useState('in_progress');
  const [recallReason, setRecallReason] = useState('');
  const [recallBusy, setRecallBusy] = useState(false);

  // Keep localJob in sync when parent passes a new job (e.g. they re-fetched)
  useEffect(() => { setLocalJob(job); }, [job]);

  // The Job pydantic response model strips the `notes` and `attachments`
  // arrays, so /api/jobs/{id} (and the list endpoint) return them as
  // undefined. We fetch them via the dedicated collab endpoints whenever
  // the dialog opens so the Notes tab actually shows what's saved on the
  // job (Feb 2026 bug fix — workers reported that notes saved from the
  // Home → "attach to job" flow were invisible on the job's Notes tab).
  const fetchJobNotes = useCallback(async () => {
    if (!localJob?.id) return;
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(
        `${API}/api/jobs/${localJob.id}/notes`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const list = (data && Array.isArray(data.notes)) ? data.notes : [];
      setLocalJob((j) => j ? { ...j, notes: list } : j);
    } catch { /* best-effort */ }
  }, [localJob?.id]);

  useEffect(() => {
    if (open && localJob?.id) {
      fetchJobNotes();
    }
  }, [open, localJob?.id, fetchJobNotes]);

  const fetchActivity = useCallback(async () => {
    if (!localJob?.id) return;
    setLoadingAct(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API}/api/jobs/${localJob.id}/activity`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setActivity(Array.isArray(data) ? data : []);
    } catch (e) {
      // Activity is best-effort — empty feed is fine if the endpoint 403s.
      setActivity([]);
    } finally {
      setLoadingAct(false);
    }
  }, [localJob?.id]);

  const refreshJob = useCallback(async () => {
    if (!localJob?.id) return;
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API}/api/jobs/${localJob.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLocalJob(data);
    } catch { /* ignore */ }
  }, [localJob?.id]);

  useEffect(() => {
    if (open && tab === 'activity') fetchActivity();
  }, [open, tab, fetchActivity]);

  // Fetch the effective rate for this job whenever the dialog opens or
  // the job's labels change. Only admins/supervisors see the billing block.
  useEffect(() => {
    if (!open || !localJob?.id || !currentUser || !(['admin', 'supervisor'].includes(currentUser.role) || currentUser.is_super_user === true)) {
      setEffectiveRate(null);
      return;
    }
    let cancelled = false;
    const token = localStorage.getItem('token');
    axios.get(`${API}/api/jobs/${localJob.id}/effective-rate`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(({ data }) => { if (!cancelled) setEffectiveRate(data); })
      .catch(() => { if (!cancelled) setEffectiveRate(null); });
    return () => { cancelled = true; };
  }, [open, localJob?.id, localJob?.label_ids, currentUser]);

  // Load watchers + the candidate admins list whenever the dialog opens for
  // a fresh job. Both are cheap so we run them in parallel.
  const fetchWatchers = useCallback(async () => {
    if (!localJob?.id) return;
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API}/api/jobs/${localJob.id}/watchers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setWatchers(Array.isArray(data) ? data : []);
    } catch {
      setWatchers([]);
    }
  }, [localJob?.id]);

  useEffect(() => {
    if (!open || !localJob?.id) return;
    fetchWatchers();
    // Candidate watchers = active admins + supervisors in the company.
    // Only admins/supervisors see this list, so it's cheap.
    if (currentUser && (['admin', 'supervisor'].includes(currentUser.role) || currentUser.is_super_user === true)) {
      const token = localStorage.getItem('token');
      axios.get(`${API}/api/users`, { headers: { Authorization: `Bearer ${token}` } })
        .then(({ data }) => {
          const list = Array.isArray(data) ? data : (data?.users || []);
          setCompanyAdmins(list.filter((u) => ['admin', 'supervisor'].includes(u.role) && u.active !== false));
        })
        .catch(() => setCompanyAdmins([]));
    }
  }, [open, localJob?.id, fetchWatchers, currentUser]);

  const isWatching = useMemo(
    () => watchers.some((w) => w.id === currentUser?.id),
    [watchers, currentUser?.id],
  );
  // Super-users are admin-equivalent for company-wide ops (mirrors canBillOut
  // and the backend permission gate). Without this OR, super-users whose
  // `role` field isn't literally "admin"/"supervisor" wouldn't see the
  // Portal tab, watcher controls, or admin-only invoice actions — even
  // though they have full company-wide privileges. (Feb 2026 bug fix.)
  const canManageWatchers = !!currentUser && (
    ['admin', 'supervisor'].includes(currentUser.role) || currentUser.is_super_user === true
  );
  // Site QR poster dialog (Jun 2026) — admins & supervisors view/print/rotate.
  const [siteQrOpen, setSiteQrOpen] = useState(false);
  // Invoice editing (Bill out) is ADMIN ONLY (Feb 2026). Workers + supervisors
  // can enter materials/receipts on the Materials tab but cannot generate the
  // final bill. Super_users are admin-equivalent for company-wide ops.
  const canBillOut = currentUser && (currentUser.role === 'admin' || currentUser.is_super_user === true);

  // Open the invoice PDF: the actual QuickBooks PDF when the job has been
  // synced, otherwise a HinesTime-generated PDF. Both endpoints require the
  // auth header, so we fetch as a blob and open it in a new tab.
  const handleViewInvoicePdf = async () => {
    if (!canBillOut || !localJob?.id) return;
    setPdfBusy(true);
    const token = localStorage.getItem('token');
    const synced = !!localJob.qb_invoice_id;
    const path = synced ? 'qb-invoice-pdf' : 'invoice-pdf';
    try {
      const { data } = await axios.get(`${API}/api/jobs/${localJob.id}/${path}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      if (!synced) onMutated && onMutated();
    } catch (e) {
      let msg = 'Could not open the invoice PDF.';
      // blob error responses need to be parsed back to JSON
      if (e.response?.data instanceof Blob) {
        try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch { /* keep default */ }
      } else {
        msg = e.response?.data?.detail || msg;
      }
      toast.error(msg);
    } finally {
      setPdfBusy(false);
    }
  };

  const handleRecall = async () => {
    if (!recallReason.trim() || recallReason.trim().length < 3) {
      toast.error('Please add a short reason for the recall.');
      return;
    }
    setRecallBusy(true);
    const token = localStorage.getItem('token');
    try {
      const { data } = await axios.put(
        `${API}/api/jobs/${localJob.id}/recall`,
        { new_status: recallStatus, reason: recallReason.trim() },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setLocalJob((j) => ({ ...j, ...(data.job || {}) }));
      toast.success('Job recalled — the assignee can pick it back up.');
      setRecallOpen(false);
      setRecallReason('');
      onMutated && onMutated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not recall this job.');
    } finally {
      setRecallBusy(false);
    }
  };

  const toggleWatchSelf = async () => {
    if (!canManageWatchers) return;
    try {
      const token = localStorage.getItem('token');
      if (isWatching) {
        await axios.delete(`${API}/api/jobs/${localJob.id}/watchers/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('You will no longer get notifications for this job');
      } else {
        await axios.post(`${API}/api/jobs/${localJob.id}/watchers/me`, null, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('You will now get notifications about this job');
      }
      fetchWatchers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update watch state');
    }
  };

  const inviteWatcher = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/jobs/${localJob.id}/watchers/${userId}`, null, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchWatchers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not add watcher');
    }
  };

  const removeWatcher = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/jobs/${localJob.id}/watchers/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchWatchers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not remove watcher');
    }
  };

  // ── Customer Portal helpers (Jun 2026) ───────────────────────────────
  // Admin / supervisor only. Generates a per-job public token the customer
  // can use to view status + photos at `/portal/{token}` and upload their
  // own photos while the job is open. Rotating invalidates the previous
  // link instantly. See routes/customer_portal.py.
  const fetchPortalInfo = useCallback(async () => {
    if (!canManageWatchers || !localJob?.id) return;
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API}/api/jobs/${localJob.id}/portal-token`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPortalInfo(data || {});
    } catch (e) {
      // 404-style errors fall through to "no token yet" rather than blocking.
      setPortalInfo({});
    }
  }, [canManageWatchers, localJob?.id]);

  // Lazy-load whenever the user navigates into the Portal tab.
  useEffect(() => {
    if (tab === 'portal' && portalInfo === null) fetchPortalInfo();
  }, [tab, portalInfo, fetchPortalInfo]);

  const generateOrRotatePortal = async () => {
    setPortalBusy(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(
        `${API}/api/jobs/${localJob.id}/portal-token`,
        null,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setPortalInfo(data);
      toast.success(data?.created_at ? 'Portal link rotated — old link disabled' : 'Portal link created');
      onMutated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not generate portal link');
    } finally {
      setPortalBusy(false);
    }
  };

  const revokePortal = async () => {
    setPortalBusy(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/jobs/${localJob.id}/portal-token`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPortalInfo({});
      toast.success('Portal link revoked');
      onMutated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not revoke portal link');
    } finally {
      setPortalBusy(false);
    }
  };

  const copyPortalUrl = async () => {
    const url = portalInfo?.url;
    if (!url) return;
    try {
      // Build an absolute URL if the backend returned a relative path
      // (happens locally if PUBLIC_PORTAL_URL env var isn't set).
      const absolute = url.startsWith('http') ? url : `${window.location.origin}${url}`;
      await navigator.clipboard.writeText(absolute);
      toast.success('Portal link copied to clipboard');
    } catch {
      toast.error('Could not copy — long-press the link to copy manually');
    }
  };

  // SMS the portal link via the existing AWS SNS pipe. The backend will
  // auto-generate a token if none exists yet, so the admin can "send"
  // without first clicking "Generate" — saving one click on the common
  // path of "I want to text this to the customer right now."
  const sendPortalSms = async () => {
    const phone = smsPhone.trim();
    if (!phone) {
      toast.error('Enter the customer\u2019s phone number first');
      return;
    }
    setSmsBusy(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(
        `${API}/api/jobs/${localJob.id}/portal-token/send-sms`,
        { phone },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Portal link texted to ${data.sent_to}`);
      setSmsPhone('');
      // If this was the first time, the backend just generated a token —
      // refresh the panel so the URL display populates.
      if (!portalInfo?.token) await fetchPortalInfo();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'SMS failed to send');
    } finally {
      setSmsBusy(false);
    }
  };

  // One-click "Send Portal Link" surface on the Overview tab. We DON'T
  // pass a phone — the backend resolves it from the linked Customer
  // record. If no phone is on file the API returns a 400; we surface the
  // error and bounce the admin to the Portal tab where they can paste a
  // number manually. This collapses the 3-click "open Portal tab → type
  // phone → send" flow into a single tap for the common case.
  const quickSendPortalSms = async () => {
    setSmsBusy(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(
        `${API}/api/jobs/${localJob.id}/portal-token/send-sms`,
        {}, // empty body — server falls back to customer.phone
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Portal link texted to ${data.sent_to}`);
      if (!portalInfo?.token) await fetchPortalInfo();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'SMS failed to send';
      toast.error(detail);
      // If the failure was "no phone on file", drop the admin onto the
      // Portal tab so they can paste a number without hunting for it.
      if (/no phone/i.test(detail)) setTab('portal');
    } finally {
      setSmsBusy(false);
    }
  };

  // Fire-and-forget mark-seen has moved to JobNotesPanel (only fires while
  // the Notes tab is active — same behavior).

  const writable = useMemo(() => canCollab(localJob, currentUser), [localJob, currentUser]);
  
  // ── Combined feed for Activity tab: server activity + notes (as messages) ──
  // IMPORTANT: This useMemo must be called before any early returns to satisfy
  // React's rules of hooks (hooks must be called in the same order every render).
  const combinedFeed = useMemo(() => {
    if (!localJob) return [];
    const fromActivity = activity.map((a) => ({ kind: 'activity', ...a }));
    const fromNotes = (localJob.notes || []).map((n) => ({
      kind: 'note',
      id: `note-${n.id}`,
      type: 'note',
      user_name: n.user_name,
      text: n.text,
      created_at: n.created_at,
    }));
    return [...fromActivity, ...fromNotes].sort((a, b) =>
      (b.created_at || '').localeCompare(a.created_at || ''),
    );
  }, [activity, localJob]);

  // ── Note actions ────────────────────────────────────────────────────────
  // All note CRUD + forward logic moved to JobNotesPanel (Feb 2026 refactor).
  // We expose a single setNotes callback so the panel can mutate the parent's
  // localJob.notes in place — keeping the Activity tab's combinedFeed in sync.
  // MUST live before any early return to satisfy React's rules of hooks.
  const setNotes = useCallback((updater) => {
    setLocalJob((j) => {
      if (!j) return j;
      const prev = j.notes || [];
      const next = typeof updater === 'function' ? updater(prev) : updater;
      return { ...j, notes: next };
    });
  }, []);

  // ── Batch presigned photo URLs (Jun 2026 perf) ─────────────────────────
  // One API call returns direct S3 URLs (full + thumbnail) for every photo,
  // replacing N per-photo backend redirect round-trips. Falls back to the
  // legacy redirect endpoint per photo when the batch call fails.
  const [photoUrlMap, setPhotoUrlMap] = useState({});
  const photoCount = localJob?.photos?.length || 0;
  useEffect(() => {
    if (!open || !localJob?.id || photoCount === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const t = localStorage.getItem('token');
        const { data } = await axios.get(`${API}/api/jobs/${localJob.id}/photo-urls`, {
          headers: t && t !== 'null' ? { Authorization: `Bearer ${t}` } : {},
        });
        if (!cancelled) {
          const map = {};
          (data.photos || []).forEach((p) => { map[p.id] = p; });
          setPhotoUrlMap(map);
        }
      } catch {
        // Non-fatal — grid falls back to the per-photo redirect endpoint.
      }
    })();
    return () => { cancelled = true; };
  }, [open, localJob?.id, photoCount]);

  // Early return AFTER all hooks are called
  if (!localJob) return null;

  const notes = localJob?.notes || [];
  const attachments = localJob?.attachments || [];

  // ── Attachment actions ──────────────────────────────────────────────────
  const uploadFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      // Auto-compress images to ≤5 MB before upload (Jun 2026, Corey).
      // Non-image attachments (PDF / Word / Excel / ZIP) are uploaded
      // as-is — they don't have a lossy compression path and the backend
      // already caps them at 25 MB.
      let toUpload = file;
      if (isCompressibleImage(file) && file.size > 800 * 1024) {
        try {
          toUpload = await compressImage(file);
        } catch {
          // Compression is best-effort; original file still uploads below.
          toUpload = file;
        }
      }
      const token = localStorage.getItem('token');
      let data;
      try {
        // Direct-to-S3 upload (Jun 2026 perf) — file bypasses the app server.
        data = await uploadJobAttachmentDirect({
          apiBase: `${API}/api`, jobId: localJob.id, token, file: toUpload,
          onProgress: setUploadPct,
        });
      } catch (directErr) {
        const st = directErr?.response?.status;
        if (st === 400 || st === 403) throw directErr; // real validation error — surface it
        const fd = new FormData();
        fd.append('file', toUpload);
        const res = await axios.post(
          `${API}/api/jobs/${localJob.id}/attachments`,
          fd,
          {
            headers: { Authorization: `Bearer ${token}` },
            onUploadProgress: (e) => { if (e.total) setUploadPct(Math.round((e.loaded / e.total) * 100)); },
          },
        );
        data = res.data;
      }
      setLocalJob((j) => ({ ...j, attachments: [...(j.attachments || []), data] }));
      toast.success(`Uploaded ${data.original_name}`);
      onMutated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      setUploadPct(0);
    }
  };

  const deleteAttachment = async (aid, name) => {
    if (!window.confirm(`Remove "${name}"?`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/jobs/${localJob.id}/attachments/${aid}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLocalJob((j) => ({ ...j, attachments: (j.attachments || []).filter((a) => a.id !== aid) }));
      onMutated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not delete file');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="max-w-[95vw] sm:max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
        data-testid="job-detail-dialog"
      >
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2 text-lg sm:text-xl">
            <Briefcase className="w-5 h-5 text-[#1E88E5]" />
            <span className="truncate flex-1">
              {localJob.job_number ? <span className="text-[#1E88E5] font-mono mr-2">{localJob.job_number}</span> : null}
              {localJob.title}
            </span>
            {/* Watch toggle + watchers popover — admin/supervisor only */}
            {canManageWatchers && (
              <div className="relative flex items-center gap-1 flex-shrink-0">
                {/* Bill out / Invoice Editor — ADMIN ONLY (Feb 2026). Workers + supervisors
                    enter materials on the Materials tab but cannot bill out. */}
                {canBillOut && (
                  <button
                    type="button"
                    onClick={() => setInvoiceEditorOpen(true)}
                    className="px-2 py-1 rounded-md text-xs font-medium flex items-center gap-1 transition-colors"
                    style={{
                      background: localJob.qb_invoice_id ? 'rgba(44,160,28,0.15)' : '#1A2433',
                      color: localJob.qb_invoice_id ? '#7CD672' : '#94A3B8',
                      border: `1px solid ${localJob.qb_invoice_id ? 'rgba(44,160,28,0.5)' : '#2D3748'}`,
                    }}
                    title={localJob.qb_invoice_id ? `Invoiced · QBO #${String(localJob.qb_invoice_id).slice(0, 8)}` : 'Open Invoice Editor — set rate, materials, tax, then send to QuickBooks or PDF'}
                    data-testid="job-bill-out-btn"
                  >
                    <DollarSign className="w-3.5 h-3.5" />
                    <span>{localJob.qb_invoice_id ? 'Invoiced' : 'Bill out'}</span>
                  </button>
                )}
                {/* View invoice PDF (QBO when synced, else HinesTime) — admin only */}
                {canBillOut && (
                  <button
                    type="button"
                    onClick={handleViewInvoicePdf}
                    disabled={pdfBusy}
                    className="px-2 py-1 rounded-md text-xs font-medium flex items-center gap-1 transition-colors disabled:opacity-50"
                    style={{ background: '#1A2433', color: '#94A3B8', border: '1px solid #2D3748' }}
                    title={localJob.qb_invoice_id ? 'View the QuickBooks invoice PDF' : 'View a HinesTime invoice PDF (job not yet synced to QuickBooks)'}
                    data-testid="job-view-invoice-pdf-btn"
                  >
                    <FileText className="w-3.5 h-3.5" />
                    <span>{pdfBusy ? 'Opening…' : 'Invoice PDF'}</span>
                  </button>
                )}
                {/* Recall a completed job — admin only */}
                {canBillOut && localJob.status === 'completed' && (
                  <button
                    type="button"
                    onClick={() => { setRecallStatus('in_progress'); setRecallReason(''); setRecallOpen(true); }}
                    className="px-2 py-1 rounded-md text-xs font-medium flex items-center gap-1 transition-colors"
                    style={{ background: 'rgba(255,160,0,0.12)', color: '#F0A020', border: '1px solid rgba(255,160,0,0.4)' }}
                    title="Recall this completed job back to an active status"
                    data-testid="job-recall-btn"
                  >
                    <RotateCw className="w-3.5 h-3.5" />
                    <span>Recall</span>
                  </button>
                )}
                {/* Site QR poster — admins & supervisors view/print/rotate */}
                <button
                  type="button"
                  onClick={() => setSiteQrOpen(true)}
                  className="px-2 py-1 rounded-md text-xs font-medium flex items-center gap-1 transition-colors"
                  style={{
                    background: localJob.site_qr_required ? 'rgba(30,136,229,0.12)' : '#1A2433',
                    color: localJob.site_qr_required ? '#7BB8E8' : '#94A3B8',
                    border: `1px solid ${localJob.site_qr_required ? 'rgba(30,136,229,0.5)' : '#2D3748'}`,
                  }}
                  title={localJob.site_qr_required ? 'View / print the site QR poster workers scan to clock in' : 'Generate a site QR poster workers scan to clock in'}
                  data-testid="job-site-qr-btn"
                >
                  <QrCode className="w-3.5 h-3.5" />
                  <span>Site QR</span>
                </button>
                <button
                  type="button"
                  onClick={toggleWatchSelf}
                  className="px-2 py-1 rounded-md text-xs font-medium flex items-center gap-1 transition-colors"
                  style={{
                    background: isWatching ? 'rgba(30,136,229,0.15)' : '#1A2433',
                    color: isWatching ? '#7BB8E8' : '#94A3B8',
                    border: `1px solid ${isWatching ? 'rgba(30,136,229,0.5)' : '#2D3748'}`,
                  }}
                  title={isWatching ? 'Stop watching — you will no longer get notifications for this job' : 'Watch — get notifications for this job'}
                  data-testid="job-watch-toggle"
                >
                  {isWatching ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                  <span>{isWatching ? 'Watching' : 'Watch'}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setWatcherPickerOpen((v) => !v)}
                  className="px-2 py-1 rounded-md text-xs font-medium flex items-center gap-1 transition-colors"
                  style={{ background: '#1A2433', color: '#94A3B8', border: '1px solid #2D3748' }}
                  title="Manage watchers"
                  data-testid="job-watchers-manage"
                >
                  <UserIcon className="w-3.5 h-3.5" />
                  <span>{watchers.length}</span>
                </button>
                {watcherPickerOpen && (
                  <div
                    className="absolute top-full right-0 mt-1 w-72 rounded-lg shadow-2xl z-50 overflow-hidden"
                    style={{ background: '#0F1923', border: '1px solid #2D3748' }}
                    data-testid="job-watchers-panel"
                  >
                    <div className="flex items-center justify-between px-3 py-2 border-b" style={{ borderColor: '#2D3748' }}>
                      <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#94A3B8' }}>
                        Watchers ({watchers.length})
                      </span>
                      <button
                        onClick={() => setWatcherPickerOpen(false)}
                        className="text-slate-400 hover:text-white"
                        aria-label="Close"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                      {watchers.length === 0 ? (
                        <p className="text-xs px-3 py-3" style={{ color: '#64748B' }}>
                          No one is watching this job yet.
                        </p>
                      ) : (
                        watchers.map((w) => (
                          <div
                            key={w.id}
                            className="flex items-center justify-between px-3 py-2 hover:bg-white/5 group"
                            data-testid={`job-watcher-row-${w.id}`}
                          >
                            <div className="min-w-0">
                              <p className="text-xs text-white truncate">{w.name}</p>
                              <p className="text-[10px]" style={{ color: '#64748B' }}>{w.role}</p>
                            </div>
                            <button
                              onClick={() => removeWatcher(w.id)}
                              className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 text-[10px] uppercase tracking-wider"
                              title={`Remove ${w.name}`}
                              data-testid={`job-watcher-remove-${w.id}`}
                            >
                              Remove
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                    {/* Invite dropdown — admins/supervisors not yet watching */}
                    <div className="px-3 py-2 border-t" style={{ borderColor: '#2D3748' }}>
                      <p className="text-[10px] uppercase tracking-wider mb-1" style={{ color: '#64748B' }}>
                        Add a watcher
                      </p>
                      <select
                        onChange={(e) => {
                          const uid = e.target.value;
                          if (uid) {
                            inviteWatcher(uid);
                            e.target.value = '';
                          }
                        }}
                        defaultValue=""
                        className="w-full px-2 py-1.5 rounded text-xs text-white"
                        style={{ background: '#1A2433', border: '1px solid #2D3748' }}
                        data-testid="job-watcher-invite-select"
                      >
                        <option value="">Pick admin or supervisor…</option>
                        {companyAdmins
                          .filter((u) => !watchers.some((w) => w.id === u.id))
                          .map((u) => (
                            <option key={u.id} value={u.id}>{u.name} ({u.role})</option>
                          ))}
                      </select>
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogTitle>
          <DialogDescription className="text-xs sm:text-sm flex flex-wrap items-center gap-2">
            {localJob.customer_name && <span>· {localJob.customer_name}</span>}
            {localJob.status && (
              <span className="px-2 py-0.5 rounded-full text-[10px] uppercase font-semibold" style={{ background: '#1A2433', color: '#94A3B8' }}>
                {localJob.status.replace(/_/g, ' ')}
              </span>
            )}
            {localJob.photos_overridden && (
              <span className="px-2 py-0.5 rounded-full text-[10px] uppercase font-semibold inline-flex items-center gap-1" style={{ background: 'rgba(255,160,0,0.12)', color: '#F0A020', border: '1px solid rgba(255,160,0,0.4)' }} title="An admin completed this job without the required photos" data-testid="job-detail-photos-overridden-badge">
                <AlertCircle className="w-3 h-3" /> Completed without photos
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="flex-1 flex flex-col overflow-hidden">
          <TabsList
            className="flex w-full overflow-x-auto gap-1.5 p-1.5 h-auto min-h-[3rem] bg-[#0F1923]/60 rounded-lg border border-[#2D3748] mb-3 flex-shrink-0 scrollbar-thin"
            data-testid="job-detail-tabs"
          >
            <TabsTrigger
              value="overview"
              data-testid="job-tab-overview"
              className="flex-1 min-w-[5.5rem] whitespace-nowrap rounded-md border border-[#2D3748] bg-[#1A2433] text-slate-300 px-3 py-2 text-xs sm:text-sm font-medium transition-colors hover:bg-[#243043] data-[state=active]:bg-[#1E88E5] data-[state=active]:text-white data-[state=active]:border-[#1E88E5] data-[state=active]:shadow"
            >
              <FileText className="w-4 h-4 mr-1.5" /> Overview
            </TabsTrigger>
            <TabsTrigger
              value="materials"
              data-testid="job-tab-materials"
              className="flex-1 min-w-[5.5rem] whitespace-nowrap rounded-md border border-[#2D3748] bg-[#1A2433] text-slate-300 px-3 py-2 text-xs sm:text-sm font-medium transition-colors hover:bg-[#243043] data-[state=active]:bg-[#1E88E5] data-[state=active]:text-white data-[state=active]:border-[#1E88E5] data-[state=active]:shadow"
            >
              <Package className="w-4 h-4 mr-1.5" /> Materials
              {(localJob.materials || []).length > 0 && <span className="ml-1.5 text-[10px] px-1.5 rounded-full bg-emerald-500/20 text-emerald-300">{(localJob.materials || []).length}</span>}
            </TabsTrigger>
            <TabsTrigger
              value="notes"
              data-testid="job-tab-notes"
              className="flex-1 min-w-[5.5rem] whitespace-nowrap rounded-md border border-[#2D3748] bg-[#1A2433] text-slate-300 px-3 py-2 text-xs sm:text-sm font-medium transition-colors hover:bg-[#243043] data-[state=active]:bg-[#1E88E5] data-[state=active]:text-white data-[state=active]:border-[#1E88E5] data-[state=active]:shadow"
            >
              <MessageSquare className="w-4 h-4 mr-1.5" /> Notes
              {notes.length > 0 && <span className="ml-1.5 text-[10px] px-1.5 rounded-full bg-blue-500/20 text-blue-300">{notes.length}</span>}
            </TabsTrigger>
            <TabsTrigger
              value="attachments"
              data-testid="job-tab-attachments"
              className="flex-1 min-w-[5.5rem] whitespace-nowrap rounded-md border border-[#2D3748] bg-[#1A2433] text-slate-300 px-3 py-2 text-xs sm:text-sm font-medium transition-colors hover:bg-[#243043] data-[state=active]:bg-[#1E88E5] data-[state=active]:text-white data-[state=active]:border-[#1E88E5] data-[state=active]:shadow"
            >
              <Paperclip className="w-4 h-4 mr-1.5" /> Files
              {attachments.length > 0 && <span className="ml-1.5 text-[10px] px-1.5 rounded-full bg-green-500/20 text-green-300">{attachments.length}</span>}
            </TabsTrigger>
            <TabsTrigger
              value="photos"
              data-testid="job-tab-photos"
              className="flex-1 min-w-[5.5rem] whitespace-nowrap rounded-md border border-[#2D3748] bg-[#1A2433] text-slate-300 px-3 py-2 text-xs sm:text-sm font-medium transition-colors hover:bg-[#243043] data-[state=active]:bg-[#1E88E5] data-[state=active]:text-white data-[state=active]:border-[#1E88E5] data-[state=active]:shadow"
            >
              <ImageIcon className="w-4 h-4 mr-1.5" /> Photos
              {(localJob.photos || []).length > 0 && <span className="ml-1.5 text-[10px] px-1.5 rounded-full bg-amber-500/20 text-amber-300">{(localJob.photos || []).length}</span>}
            </TabsTrigger>
            <TabsTrigger
              value="activity"
              data-testid="job-tab-activity"
              className="flex-1 min-w-[5.5rem] whitespace-nowrap rounded-md border border-[#2D3748] bg-[#1A2433] text-slate-300 px-3 py-2 text-xs sm:text-sm font-medium transition-colors hover:bg-[#243043] data-[state=active]:bg-[#1E88E5] data-[state=active]:text-white data-[state=active]:border-[#1E88E5] data-[state=active]:shadow"
            >
              <ActivityIcon className="w-4 h-4 mr-1.5" /> Activity
            </TabsTrigger>
            {canManageWatchers && (
              <TabsTrigger
                value="portal"
                data-testid="job-tab-portal"
                className="flex-1 min-w-[5.5rem] whitespace-nowrap rounded-md border border-[#2D3748] bg-[#1A2433] text-slate-300 px-3 py-2 text-xs sm:text-sm font-medium transition-colors hover:bg-[#243043] data-[state=active]:bg-[#1E88E5] data-[state=active]:text-white data-[state=active]:border-[#1E88E5] data-[state=active]:shadow"
              >
                <Link2 className="w-4 h-4 mr-1.5" /> Portal
                {portalInfo?.token && (
                  <span className="ml-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.8)]" />
                )}
              </TabsTrigger>
            )}
          </TabsList>

          {/* OVERVIEW */}
          <TabsContent value="overview" className="flex-1 overflow-y-auto p-1 pt-4 space-y-3" data-testid="job-overview-panel">
            <Row icon={UserIcon} label={localJob.crew_names && !localJob.is_team_job && Object.keys(localJob.crew_names).length > 1 ? 'Assignees' : 'Assignee'}
              value={localJob.crew_names && !localJob.is_team_job && Object.keys(localJob.crew_names).length > 1 ? Object.values(localJob.crew_names).join(', ') : (localJob.assigned_to_name || localJob.assigned_user_name || (localJob.crew_names && localJob.crew_names[localJob.assigned_to]) || '—')} />
            <Row icon={Calendar} label="Scheduled" value={localJob.scheduled_start ? new Date(localJob.scheduled_start).toLocaleString() : '—'} />
            <Row icon={MapPin} label="Address" value={localJob.job_address || localJob.street_address || '—'} />
            {canManageWatchers && <CrewHoursPanel jobId={localJob.id} refreshKey={localJob.total_hours_worked} />}

            {/* Customer Portal — one-click SMS shortcut for admins. Sits up
                here on Overview so the admin doesn't have to hunt for the
                Portal tab on the common "text the customer their link
                right now" flow. Backend resolves phone from the linked
                Customer record; falls back to the Portal tab if missing. */}
            {canManageWatchers && (
              <div
                className="flex items-center gap-3 rounded-lg px-3 py-2.5"
                style={{ background: 'rgba(56,189,248,0.06)', border: '1px solid rgba(56,189,248,0.18)' }}
                data-testid="overview-portal-quick-send"
              >
                <Link2 className="w-4 h-4 flex-shrink-0" style={{ color: '#38BDF8' }} />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#64748B' }}>
                    Customer Portal
                  </p>
                  <p className="text-xs" style={{ color: '#94A3B8' }}>
                    Text the customer a secure link so they can monitor this job.
                  </p>
                </div>
                <Button
                  size="sm"
                  onClick={quickSendPortalSms}
                  disabled={smsBusy}
                  className="flex-shrink-0"
                  style={{ background: '#0EA5E9' }}
                  data-testid="overview-portal-send-btn"
                >
                  <Send className="w-3.5 h-3.5 mr-1.5" />
                  {smsBusy ? 'Sending…' : 'Send link'}
                </Button>
              </div>
            )}

            {/* QBO Invoice deep-link — appears once the job has been pushed
                to QuickBooks. Shows the DocNumber QBO actually assigned
                (which equals the HinesTime job number when the realm has
                custom transaction numbers enabled) and links straight into
                the QBO web app so admins can reconcile in one click. */}
            {canManageWatchers && localJob.qb_invoice_id && (
              <a
                href={`https://app.qbo.intuit.com/app/invoice?txnId=${encodeURIComponent(localJob.qb_invoice_id)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 rounded-lg px-3 py-2.5 group hover:bg-emerald-500/10 transition-colors"
                style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.22)' }}
                data-testid="overview-qbo-invoice-link"
              >
                <ReceiptIcon className="w-4 h-4 flex-shrink-0" style={{ color: '#10B981' }} />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#64748B' }}>
                    QuickBooks Invoice
                  </p>
                  <p className="text-sm font-mono" style={{ color: '#E2E8F0' }}>
                    #{localJob.qb_invoice_doc_number || localJob.qb_invoice_id}
                  </p>
                  {localJob.qb_synced_at && (
                    <p className="text-[11px]" style={{ color: '#64748B' }}>
                      Synced {fmt(localJob.qb_synced_at)}
                    </p>
                  )}
                </div>
                <span className="text-xs flex-shrink-0 group-hover:underline" style={{ color: '#10B981' }}>
                  Open in QBO ↗
                </span>
              </a>
            )}

            {/* Safety attestation — captured at Start. Read-only here.
                Renders whenever the worker has acknowledged the OH&S /
                OSHA fit-for-duty checklist. Shows attester name + team
                context + version + timestamp for audit. */}
            {localJob.safety_attestation && (
              <div
                className="flex items-start gap-3 rounded-lg p-3"
                style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.22)' }}
                data-testid="overview-safety-attestation"
              >
                <ShieldCheck className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#10B981' }} />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#64748B' }}>
                    Safety attestation
                  </p>
                  <p className="text-sm mt-0.5" style={{ color: '#E2E8F0' }}>
                    Acknowledged by{' '}
                    <span className="font-semibold">
                      {localJob.safety_attestation.accepted_by_name || 'worker'}
                    </span>
                    {localJob.safety_attestation.accepted_for_team && localJob.safety_attestation.team_name && (
                      <> on behalf of team{' '}
                        <span className="font-semibold">{localJob.safety_attestation.team_name}</span>
                      </>
                    )}
                  </p>
                  <p className="text-[11px] mt-1" style={{ color: '#64748B' }}>
                    {fmt(localJob.safety_attestation.accepted_at)}
                    {localJob.safety_attestation.version ? ` · v${localJob.safety_attestation.version}` : ''}
                    {' · '}{(localJob.safety_attestation.items || []).length} item
                    {(localJob.safety_attestation.items || []).length === 1 ? '' : 's'} confirmed
                  </p>
                </div>
              </div>
            )}

            {/* Travel Time (Feb 2026) — explicit Start/Stop travel buttons
                + a live summary of all travel segments on this job. Hidden
                until the job has been started or has an existing segment
                so the overview stays clean for newly-created todos. */}
            {(localJob.status !== 'pending' && localJob.status !== 'todo') || (localJob.travel_segments?.length > 0) ? (
              <div
                className="rounded-lg p-3"
                style={{ background: 'rgba(245,158,11,0.04)', border: '1px solid rgba(245,158,11,0.18)' }}
                data-testid="overview-travel-time"
              >
                <TravelTimeControls jobId={localJob.id} siteQrRequired={localJob.site_qr_required} onChange={() => onMutated?.()} />
              </div>
            ) : null}

            {/* Customer satisfaction — populated by the customer via the
                public portal once the job is completed. Read-only here;
                only renders when a rating exists. */}
            {localJob.customer_rating && (
              <div
                className="flex items-start gap-3 rounded-lg p-3"
                style={{
                  background: localJob.customer_rating === 'up'
                    ? 'rgba(16,185,129,0.08)' : 'rgba(244,63,94,0.08)',
                  border: localJob.customer_rating === 'up'
                    ? '1px solid rgba(16,185,129,0.25)' : '1px solid rgba(244,63,94,0.25)',
                }}
                data-testid="overview-customer-rating"
              >
                <span className="text-lg flex-shrink-0 mt-0.5">
                  {localJob.customer_rating === 'up' ? '👍' : '👎'}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#64748B' }}>
                    Customer rated this job
                  </p>
                  {localJob.customer_rating_comment ? (
                    <p className="text-sm mt-1 whitespace-pre-line" style={{ color: '#E2E8F0' }}>
                      &ldquo;{localJob.customer_rating_comment}&rdquo;
                    </p>
                  ) : (
                    <p className="text-xs italic mt-1" style={{ color: '#94A3B8' }}>
                      No comment provided.
                    </p>
                  )}
                  {localJob.customer_rated_at && (
                    <p className="text-[11px] mt-1" style={{ color: '#64748B' }}>
                      {fmt(localJob.customer_rated_at)}
                    </p>
                  )}
                </div>
              </div>
            )}
            {/* Labels — rate tags (Emergency / Night / Day / etc.). Admin & supervisor can edit. */}
            <div className="flex gap-3 items-start pt-1">
              <Tag className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#64748B' }} />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#64748B' }}>Labels</p>
                <JobLabelsEditor
                  jobId={localJob.id}
                  value={localJob.label_ids || []}
                  canEdit={canManageWatchers}
                  canManageCatalog={currentUser && (currentUser.role === 'admin' || currentUser.is_super_user)}
                  onChange={(newIds, newLabels) => {
                    setLocalJob((prev) => prev ? { ...prev, label_ids: newIds, labels: newLabels } : prev);
                  }}
                />
              </div>
            </div>
            {/* Effective billing rate — resolved from labels (highest wins, no
                stacking). Admin/Supervisor only. Falls back to the job's
                service_rate when no labels carry a rate. */}
            {effectiveRate && canManageWatchers && (
              <div
                className="flex gap-3 items-start rounded-lg p-3"
                style={{ background: 'rgba(0,200,150,0.06)', border: '1px solid rgba(0,200,150,0.18)' }}
                data-testid="effective-rate-block"
              >
                <span className="text-base flex-shrink-0 mt-0.5">💰</span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: '#64748B' }}>
                    Effective billing rate
                  </p>
                  {effectiveRate.source === 'label' && (
                    <>
                      <p className="text-sm" style={{ color: '#E2E8F0' }}>
                        <span className="font-mono font-bold text-emerald-400" data-testid="effective-rate-value">
                          ${Number(effectiveRate.effective_rate || 0).toFixed(2)}/hr
                        </span>
                        {' '}
                        <span style={{ color: '#94A3B8' }}>
                          via {effectiveRate.source_label?.name}
                          {effectiveRate.rate_type === 'multiplier' && effectiveRate.base_rate > 0 && (
                            <> ({Number(effectiveRate.rate_value)}× ${Number(effectiveRate.base_rate).toFixed(2)} base)</>
                          )}
                          {effectiveRate.rate_type === 'fixed' && <> (fixed rate)</>}
                        </span>
                      </p>
                      {Array.isArray(effectiveRate.candidates) && effectiveRate.candidates.length > 1 && (
                        <p className="text-[11px] mt-1" style={{ color: '#64748B' }} data-testid="effective-rate-breakdown">
                          Highest of: {effectiveRate.candidates.map((c) => `${c.label_name} $${c.resolved_rate.toFixed(2)}`).join(' · ')}
                        </p>
                      )}
                    </>
                  )}
                  {effectiveRate.source === 'service_rate' && (
                    <p className="text-sm" style={{ color: '#94A3B8' }}>
                      <span className="font-mono font-bold" style={{ color: '#E2E8F0' }}>
                        ${Number(effectiveRate.base_rate || 0).toFixed(2)}/hr
                      </span>{' '}from the job's base service rate. Add a rate-bearing label to override.
                    </p>
                  )}
                  {effectiveRate.source === 'none' && (
                    <p className="text-sm italic" style={{ color: '#94A3B8' }}>
                      No billing rate set. Add a base service rate or a rate-bearing label.
                    </p>
                  )}
                </div>
              </div>
            )}
            {localJob.description && (
              <div className="pt-2">
                <p className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: '#64748B' }}>Description</p>
                <p className="text-sm whitespace-pre-wrap" style={{ color: '#CBD5E1' }}>{localJob.description}</p>
              </div>
            )}
            {localJob.paused_reason && localJob.status === 'paused' && (
              <div className="rounded-lg p-3" style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)' }}>
                <p className="text-xs font-semibold text-amber-400">Paused: {localJob.paused_reason}</p>
                {localJob.paused_notes && <p className="text-xs mt-1" style={{ color: '#FCD34D' }}>{localJob.paused_notes}</p>}
              </div>
            )}
            {!writable && (
              <p className="text-xs italic" style={{ color: '#64748B' }}>
                You can view this job but need to be assigned to add notes or files.
              </p>
            )}
          </TabsContent>

          {/* MATERIALS — workers + supervisors + admins enter materials they
              purchased for this job (description, qty, cost, receipt photo).
              Admin uses these when generating the invoice. Read-only when the
              viewer can't edit (e.g., archived users, viewers). */}
          <TabsContent value="materials" className="flex-1 overflow-y-auto p-1 pt-4" data-testid="job-materials-panel">
            <MaterialsPanel
              job={localJob}
              token={localStorage.getItem('token')}
              canEdit={canCollab(localJob, currentUser)}
              onMutated={() => { refreshJob(); onMutated?.(); }}
            />
          </TabsContent>

          {/* NOTES — extracted into JobNotesPanel (Feb 2026) */}
          <TabsContent value="notes" className="flex-1 overflow-y-auto p-1 pt-4" data-testid="job-notes-panel">
            <JobNotesPanel
              jobId={localJob.id}
              notes={notes}
              setNotes={setNotes}
              currentUser={currentUser}
              writable={writable}
              isActive={open && tab === 'notes'}
              onMutated={onMutated}
            />
          </TabsContent>

          {/* ATTACHMENTS */}
          <TabsContent value="attachments" className="flex-1 overflow-y-auto p-1 pt-4" data-testid="job-attachments-panel">
            {writable && (
              <label
                className={`flex items-center justify-center gap-2 px-3 py-2 mb-3 rounded-lg text-sm font-medium cursor-pointer transition-colors ${uploading ? 'opacity-60 cursor-wait' : 'hover:bg-blue-700'}`}
                style={{ background: '#1E88E5', color: 'white' }}
                data-testid="job-attachment-upload-label"
              >
                <Upload className="w-4 h-4" />
                {uploading ? (uploadPct > 0 ? `Uploading… ${uploadPct}%` : 'Uploading…') : 'Upload file (PDF · Word · Excel · ZIP · image, max 25 MB)'}
                <input
                  type="file"
                  className="hidden"
                  disabled={uploading}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) uploadFile(f);
                    e.target.value = '';
                  }}
                  data-testid="job-attachment-upload-input"
                />
              </label>
            )}
            {attachments.length > 1 && (
              <a
                href={zipHref(localJob.id)}
                className="flex items-center justify-center gap-2 px-3 py-2 mb-3 rounded-lg text-sm font-medium border transition-colors hover:bg-white/5"
                style={{ borderColor: '#2D3748', color: '#CBD5E1' }}
                data-testid="job-download-all-attachments"
              >
                <FileArchive className="w-4 h-4" /> Download all ({attachments.length}) as ZIP
              </a>
            )}
            {attachments.length === 0 ? (
              <p className="text-sm text-center py-8" style={{ color: '#64748B' }}>
                No files attached. {writable ? 'Upload estimates, drawings, change orders, etc.' : ''}
              </p>
            ) : (
              <ul className="space-y-2">
                {[...attachments].reverse().map((a) => {
                  const Icon = iconForAttachment(a);
                  const archive = isArchive(a);
                  const mine = a.uploaded_by_id === currentUser?.id;
                  const canDelete = mine || ['admin', 'supervisor'].includes(currentUser?.role);
                  return (
                    <li
                      key={a.id}
                      className="flex items-center gap-3 p-2.5 rounded-lg"
                      style={{ background: '#0F1923', border: '1px solid #2D3748' }}
                      data-testid={`job-attachment-${a.id}`}
                    >
                      <Icon className="w-5 h-5 flex-shrink-0" style={{ color: '#94A3B8' }} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white truncate" title={a.original_name}>
                          {a.original_name}
                        </p>
                        <div className="flex items-center gap-2 mt-0.5 text-[10px] flex-wrap" style={{ color: '#64748B' }}>
                          {archive && (
                            <span
                              className="px-1.5 py-0.5 rounded font-semibold inline-flex items-center gap-0.5"
                              style={{ color: '#FCD34D', background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.3)' }}
                              title="Archive — scan with antivirus before opening"
                            >
                              <ShieldAlert className="w-2.5 h-2.5" /> Scan before opening
                            </span>
                          )}
                          <span>{fmtSize(a.size)}</span>
                          {a.uploaded_by_name && <span>· {a.uploaded_by_name}</span>}
                          {a.uploaded_at && (
                            <time dateTime={a.uploaded_at} title={fmtFull(a.uploaded_at)} data-testid={`job-attachment-timestamp-${a.id}`}>
                              · {fmt(a.uploaded_at)}
                            </time>
                          )}
                        </div>
                      </div>
                      <a
                        href={attachmentHref(a)}
                        download={a.original_name}
                        target="_blank"
                        rel="noreferrer"
                        className="p-1.5 rounded hover:bg-white/5 flex-shrink-0"
                        style={{ color: '#94A3B8' }}
                        title="Download"
                        data-testid={`job-attachment-download-${a.id}`}
                      >
                        <Download className="w-4 h-4" />
                      </a>
                      {canDelete && (
                        <button
                          onClick={() => deleteAttachment(a.id, a.original_name)}
                          className="p-1.5 rounded hover:bg-red-500/10 flex-shrink-0 text-red-400 hover:text-red-300"
                          title="Remove file"
                          data-testid={`job-attachment-delete-${a.id}`}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </TabsContent>

          {/* PHOTOS — field-tech job photos (job.photos), view + full-size lightbox */}
          <TabsContent value="photos" className="flex-1 overflow-y-auto p-1 pt-4" data-testid="job-photos-panel">
            {['admin', 'supervisor'].includes(currentUser?.role) && (
              <div className="mb-4 rounded-xl p-3" style={{ background: '#0B1321', border: '1px solid #2D3748' }} data-testid="job-detail-admin-uploader">
                <p className="text-xs font-semibold text-slate-200 mb-2">Add photos for this job <span className="font-normal text-slate-500">— e.g. ones a worker texted or emailed you</span></p>
                <AdminPhotoUploader
                  job={localJob}
                  token={localStorage.getItem('token')}
                  compact
                  onUploaded={(photos) => {
                    setLocalJob((j) => (j ? { ...j, photos: [...(j.photos || []), ...photos] } : j));
                    refreshJob();
                    onMutated?.();
                  }}
                />
              </div>
            )}
            {(localJob.photos || []).length === 0 ? (
              <p className="text-sm text-center py-8" style={{ color: '#64748B' }}>
                No photos uploaded for this job.
              </p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {(localJob.photos || []).map((photo, idx) => {
                  const token = localStorage.getItem('token');
                  const q = token ? `?token=${encodeURIComponent(token)}` : '';
                  // Grid tiles prefer the direct-S3 thumbnail from the batch
                  // photo-urls call; fall back to the legacy redirect endpoint.
                  const batch = photoUrlMap[photo.id];
                  const src = batch?.thumb_url || batch?.url
                    || `${API}/api/jobs/${localJob.id}/photos/${photo.filename}${q ? `${q}&thumb=1` : '?thumb=1'}`;
                  return (
                    <div key={photo.id || photo.filename} className="min-w-0">
                      <button
                        type="button"
                        onClick={() => setLightboxPhoto({
                          images: (localJob.photos || []).map((p) => ({
                            src: photoUrlMap[p.id]?.url || `${API}/api/jobs/${localJob.id}/photos/${p.filename}${q}`,
                            name: photoDisplayName(p),
                            id: p.id || p.filename,
                          })),
                          index: idx,
                        })}
                        className="relative aspect-square w-full rounded-lg overflow-hidden border border-[#2D3748] hover:border-[#1E88E5] transition-colors group"
                        data-testid={`job-detail-photo-${photo.id || photo.filename}`}
                      >
                        <PhotoThumb src={src} alt={photo.original_filename || 'Job photo'} />
                      </button>
                      <PhotoMetaBar
                        jobId={localJob.id}
                        photo={photo}
                        onUpdated={(updated) => setLocalJob((j) => j ? {
                          ...j,
                          photos: (j.photos || []).map((p) => (p.id === updated.id ? { ...p, ...updated } : p)),
                        } : j)}
                      />
                      {(photo.uploaded_on_behalf || photo.taken_by_name) && (
                        <p className="text-[10px] mt-0.5 truncate" style={{ color: '#94A3B8' }} data-testid={`job-detail-photo-onbehalf-${photo.id}`}
                           title={`Uploaded by ${photo.uploaded_by_name}${photo.taken_by_name ? ` · taken by ${photo.taken_by_name}` : ''}${photo.received_via ? ` · via ${RECEIVED_VIA_LABEL[photo.received_via] || photo.received_via}` : ''}`}>
                          {photo.taken_by_name ? `Taken by ${photo.taken_by_name}` : `Added by ${photo.uploaded_by_name}`}{photo.received_via ? ` · ${RECEIVED_VIA_LABEL[photo.received_via] || photo.received_via}` : ''}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* ACTIVITY — Timeline view with avatars + day separators (Jun 2026) */}
          <TabsContent value="activity" className="flex-1 overflow-y-auto p-1 pt-4" data-testid="job-activity-panel">
            {loadingAct ? (
              <p className="text-sm text-center py-8" style={{ color: '#64748B' }}>Loading activity…</p>
            ) : combinedFeed.length === 0 ? (
              <p className="text-sm text-center py-8" style={{ color: '#64748B' }}>
                No activity recorded yet.
              </p>
            ) : (
              <ol className="relative" data-testid="job-activity-timeline">
                {/* Continuous vertical line that connects every avatar bubble */}
                <span
                  aria-hidden
                  className="absolute left-[15px] top-2 bottom-2 w-px"
                  style={{ background: 'linear-gradient(to bottom, transparent, #2D3748 8%, #2D3748 92%, transparent)' }}
                />
                {combinedFeed.map((entry, idx) => {
                  const prev = combinedFeed[idx - 1];
                  const curDay = dayLabel(entry.created_at);
                  const prevDay = prev ? dayLabel(prev.created_at) : null;
                  const showDay = curDay && curDay !== prevDay;
                  const Icon = ACTIVITY_ICON_MAP[entry.type] || Clock;
                  const accent = ACTIVITY_COLOR[entry.type] || '#64748B';
                  const avatarBg = colorForUser(entry.user_name);
                  return (
                    <li key={entry.id} className="relative pl-10 pb-3" data-testid={`job-activity-${entry.id}`}>
                      {/* Day separator pill */}
                      {showDay && (
                        <div
                          className="ml-[-2.5rem] mb-2 mt-1 flex items-center gap-2"
                          data-testid={`job-activity-daysep-${curDay.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          <span className="flex-1 h-px" style={{ background: '#1A2433' }} />
                          <span
                            className="px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-full font-semibold"
                            style={{ background: '#0F1923', border: '1px solid #2D3748', color: '#94A3B8' }}
                          >
                            {curDay}
                          </span>
                          <span className="flex-1 h-px" style={{ background: '#1A2433' }} />
                        </div>
                      )}
                      {/* Avatar bubble + small event-type pip */}
                      <div className="absolute left-0 top-0.5 flex items-center justify-center">
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold text-white shadow-md"
                          style={{ background: avatarBg, boxShadow: '0 0 0 3px #0B0F14' }}
                          title={entry.user_name}
                        >
                          {initialsOf(entry.user_name)}
                        </div>
                        {/* Event-type pip pinned to the bottom-right of the avatar */}
                        <span
                          className="absolute -bottom-0.5 -right-1 w-4 h-4 rounded-full flex items-center justify-center"
                          style={{ background: '#0B0F14', border: `1px solid ${accent}` }}
                          aria-hidden
                        >
                          <Icon className="w-2.5 h-2.5" style={{ color: accent }} />
                        </span>
                      </div>
                      {/* Event body */}
                      <div
                        className="p-2.5 rounded-lg"
                        style={{ background: '#0F1923', border: '1px solid #2D3748' }}
                      >
                        <p className="text-sm text-white">
                          <span className="font-semibold">{entry.user_name}</span>{' '}
                          <span style={{ color: '#94A3B8' }}>{entry.text}</span>
                        </p>
                        {entry.created_at && (
                          <time
                            className="text-[10px]"
                            style={{ color: '#64748B' }}
                            dateTime={entry.created_at}
                            title={fmtFull(entry.created_at)}
                          >
                            {fmt(entry.created_at)}
                          </time>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
            <div className="mt-3 flex justify-end">
              <button
                onClick={() => { refreshJob(); fetchActivity(); }}
                className="text-xs underline"
                style={{ color: '#64748B' }}
                data-testid="job-activity-refresh"
              >
                Refresh
              </button>
            </div>
          </TabsContent>

          {/* PORTAL — admin/supervisor: per-job public link the customer
              opens to view status, photos, and (while open) upload photos. */}
          {canManageWatchers && (
            <TabsContent
              value="portal"
              className="flex-1 overflow-y-auto p-1 pt-4 space-y-4"
              data-testid="job-portal-panel"
            >
              <div className="text-sm text-slate-300 leading-relaxed">
                Give your customer a private, secure link to see this job&apos;s
                status and photos — and upload their own photos while the job
                is open. Anyone with the link can view; revoke it any time
                or rotate to invalidate the previous URL instantly.
              </div>

              {portalInfo === null ? (
                <div className="text-xs text-slate-400">Loading portal link…</div>
              ) : !portalInfo.token ? (
                <div className="rounded-lg border border-dashed border-slate-600 p-4 bg-slate-900/40">
                  <div className="text-sm mb-3 text-slate-300">
                    No portal link has been generated for this job yet.
                  </div>
                  <Button
                    onClick={generateOrRotatePortal}
                    disabled={portalBusy}
                    data-testid="portal-generate-btn"
                  >
                    <Link2 className="w-4 h-4 mr-2" />
                    Generate portal link
                  </Button>
                </div>
              ) : (
                <div className="rounded-lg border border-emerald-700/50 bg-emerald-900/10 p-4 space-y-3">
                  <div className="text-xs uppercase tracking-wider font-medium text-emerald-300">
                    Active link
                  </div>
                  <div
                    className="font-mono text-xs break-all bg-black/40 rounded px-3 py-2 text-emerald-200 select-all"
                    data-testid="portal-url-display"
                  >
                    {portalInfo.url}
                  </div>
                  {portalInfo.created_at && (
                    <div className="text-[11px] text-slate-400">
                      Created {fmt(portalInfo.created_at)}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2 pt-1">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={copyPortalUrl}
                      data-testid="portal-copy-btn"
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      Copy link
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={generateOrRotatePortal}
                      disabled={portalBusy}
                      data-testid="portal-rotate-btn"
                    >
                      <RotateCw className="w-4 h-4 mr-2" />
                      Rotate
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={revokePortal}
                      disabled={portalBusy}
                      data-testid="portal-revoke-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Revoke
                    </Button>
                  </div>
                </div>
              )}

              {/* SMS the link straight to the customer — works whether or
                  not a token exists yet (backend auto-generates on first
                  send to save the admin a click). */}
              <div
                className="rounded-lg border border-slate-700 bg-slate-900/40 p-4 space-y-3"
                data-testid="portal-sms-card"
              >
                <div className="text-sm font-medium text-slate-200 flex items-center gap-2">
                  <Send className="w-4 h-4 text-sky-400" />
                  Text the link to the customer
                </div>
                <div className="text-xs text-slate-400">
                  Customers tap SMS links faster than email links — typical
                  reply time drops from hours to minutes. We use the same
                  AWS SNS pipe as the admin alerts you already see.
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Input
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    placeholder="+14165551234"
                    value={smsPhone}
                    onChange={(e) => setSmsPhone(e.target.value)}
                    disabled={smsBusy}
                    className="flex-1 bg-slate-950 border-slate-700 text-slate-100 placeholder:text-slate-500"
                    data-testid="portal-sms-phone"
                  />
                  <Button
                    onClick={sendPortalSms}
                    disabled={smsBusy || !smsPhone.trim()}
                    data-testid="portal-sms-send-btn"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {smsBusy ? 'Sending…' : 'Send via SMS'}
                  </Button>
                </div>
                <div className="text-[11px] text-slate-500">
                  Phone must include country code (E.164 format, e.g.
                  +14165551234). We don&apos;t store the number on the job.
                </div>
              </div>
            </TabsContent>
          )}
        </Tabs>
      </DialogContent>

      {/* Invoice Editor — opens on top of this dialog from the "Bill out" header button */}
      <InvoiceEditorDialog
        open={invoiceEditorOpen}
        job={localJob}
        token={localStorage.getItem('token')}
        onClose={() => setInvoiceEditorOpen(false)}
        onInvoiced={() => { refreshJob(); onMutated?.(); }}
      />

      {/* Full-size photo lightbox — shared component (zoom, prev/next, loading spinner, tap-to-retry) */}
      {lightboxPhoto && (
        <Lightbox
          images={lightboxPhoto.images}
          index={lightboxPhoto.index}
          onIndexChange={(i) => setLightboxPhoto((lb) => (lb ? { ...lb, index: i } : lb))}
          onClose={() => setLightboxPhoto(null)}
        />
      )}

      {/* Recall completed job dialog */}
      <Dialog open={recallOpen} onOpenChange={(v) => !recallBusy && setRecallOpen(v)}>
        <DialogContent className="max-w-md" data-testid="job-recall-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCw className="w-5 h-5" style={{ color: '#F0A020' }} />
              Recall Completed Job
            </DialogTitle>
            <DialogDescription>
              Move this job back to an active status so the assigned person, team, or squad can continue working on it in HinesTime.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold" style={{ color: '#64748B' }}>
                Recall to
              </label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {[
                  { v: 'todo', label: 'To Do' },
                  { v: 'in_progress', label: 'In Progress' },
                  { v: 'paused', label: 'Paused' },
                ].map((opt) => (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => setRecallStatus(opt.v)}
                    className="py-2 rounded-lg text-xs font-medium transition-colors"
                    style={{
                      background: recallStatus === opt.v ? 'rgba(30,136,229,0.15)' : '#1A2433',
                      color: recallStatus === opt.v ? '#7BB8E8' : '#94A3B8',
                      border: `1px solid ${recallStatus === opt.v ? 'rgba(30,136,229,0.6)' : '#2D3748'}`,
                    }}
                    data-testid={`recall-status-${opt.v}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-wider font-semibold" style={{ color: '#64748B' }}>
                Reason for recall
              </label>
              <textarea
                value={recallReason}
                onChange={(e) => setRecallReason(e.target.value)}
                rows={3}
                placeholder="e.g. Job was marked complete by mistake — crew still needs to finish the north wing."
                className="w-full mt-2 px-3 py-2 rounded-lg text-sm resize-none focus:outline-none"
                style={{ background: '#0F1923', border: '1px solid #2D3748', color: '#E2E8F0' }}
                data-testid="recall-reason-input"
              />
            </div>

            <div className="text-[11px] rounded-lg p-3" style={{ background: 'rgba(255,160,0,0.08)', border: '1px solid rgba(255,160,0,0.25)', color: '#C9A25A' }}>
              If this job has already been sent to QuickBooks for invoicing, you must delete the invoice in QuickBooks first — HinesTime will block the recall until then.
            </div>
          </div>

          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setRecallOpen(false)} disabled={recallBusy} data-testid="recall-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={handleRecall}
              disabled={recallBusy || !recallReason.trim()}
              style={{ background: '#F0A020', color: '#0B1321' }}
              data-testid="recall-confirm-btn"
            >
              {recallBusy ? 'Recalling…' : 'Recall Job'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Site QR poster — view / print / rotate / remove (admin & supervisor) */}
      <SiteQrDialog
        job={localJob}
        open={siteQrOpen}
        onClose={() => setSiteQrOpen(false)}
        onChanged={() => onMutated?.()}
      />
    </Dialog>
  );
}

// Tiny row used in the Overview tab
function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2 text-sm">
      {Icon && <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#64748B' }} />}
      <div className="flex-1">
        <p className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: '#64748B' }}>{label}</p>
        <p style={{ color: '#E2E8F0' }}>{value}</p>
      </div>
    </div>
  );
}
